import React, { ChangeEvent, useEffect, useState, useCallback } from 'react';
import { Button, DatePicker, ErrorText, Option, Radio, TextBlock, TextField, Checkbox } from 'neon-design-system';
import { useFormControl, useFormGroup, useManageTemplateForm } from '../../../../utils/form.utils';
import ManageTemplate, { ManageTemplateInstance } from '../../../ui/ManageTemplate';
import { ChannelSubscription, ChannelSubscriptionType, ChannelSubscriptionTypeByPayment } from '../../../../shared/types';
import ManageTemplateFormItem from '../../../ui/ManageTemplateFormItem';
import { Box, Grid } from '@material-ui/core';
import {
	PremiumChannelsSubscribersFormInterface,
	getPremiumChannelsSubscribersFormBreadCrumbs,
	getPremiumChannelsSubscribersFormCreateButtonLabel,
	getPremiumChannelsSubscribersFormInstance,
	premiumChannelsSubscribersFormDeleteButtonLabel,
	premiumChannelsSubscribersFormDeleteDialogTitle,
	premiumChannelsSubscribersFormUpdateButtonLabel,
	isFreeSubscriptionTypeByPayment,
	isPaidSubscriptionTypeByPayment,
	isFreeTrialSubscriptionTypeByPayment, styles
} from './ChannelSubscribersShared';
import {
	dateValidator, maxNumberValidator, MIN_DATE_MESSAGE, minNumberOrEmptyValidator,
	priceValidator,
	requiredValidator,
	todayOrFutureDateValidator
} from '../../../../utils/validator.utils';
import {
	getChannelSubscriptionCancelAtDateToShow,
	isAccountChannelSubscriptionType, isExternalBillingChannelSubscriptionType,
	isNonChargeChannelSubscriptionType, isSameChannelSubscriptionTypeSelected, isStripeChannelSubscriptionType,
	isSubscriptionExpired, isTrialChannelSubscriptionType
} from '../../../../utils/channelSubscriptions.utils';
import { config } from '../../../../config';
import {
	CANCEL_NON_STRIPE_SUBSCRIPTION_IMMEDIATELY,
	CREATE_ACCOUNT_CHANNEL_SUBSCRIPTION,
	CREATE_EXTERNAL_BILLING_CHANNEL_SUBSCRIPTION,
	CREATE_NON_CHARGE_CHANNEL_SUBSCRIPTION,
	CREATE_TRIAL_CHANNEL_SUBSCRIPTION,
	UPDATE_ACCOUNT_CHANNEL_SUBSCRIPTION, UPDATE_EXTERNAL_BILLING_CHANNEL_SUBSCRIPTION,
	UPDATE_NON_CHARGE_CHANNEL_SUBSCRIPTION, UPDATE_TRIAL_CHANNEL_SUBSCRIPTION
} from '../../../../gql/subscriptions';
import { getDateEndOfTheDay, getStartOfTheDay } from '../../../../utils/date.utils';
import moment from 'moment';
import RadioWithContent from '../../../ui/RadioWithContent';

export const PremiumChannelSubscribersForm = (props: PremiumChannelsSubscribersFormInterface) => {
	const classes = styles();

	const {
		channelId,
		userFormControl,
		notesFormControl,
		fileFormControl,
		returnUrl,
		isEdit,
		isBulk,
		channel,
		proceedBulk,
		usersField,
		notesField,
		currentUser,
		onDelete
	} = props;

	const [channelSubscription, setChannelSubscription] = useState<ChannelSubscription | undefined>();

	const subscriptionTypeByPaymentFormControl = useFormControl<ChannelSubscriptionTypeByPayment | undefined>(undefined, [requiredValidator]);
	const subscriptionTypeFormControl = useFormControl<ChannelSubscriptionType | undefined>(undefined, []);
	const accountNumberControl = useFormControl<string | undefined>('', []);
	const expiryDateFormControl = useFormControl<Date | null>(null, [todayOrFutureDateValidator, dateValidator]);
	const pricePerMonthControl = useFormControl<string>('', [priceValidator]);
	const isFullArticleContentControl = useFormControl<boolean>(false, []);
	const billingMethodControl = useFormControl<string | undefined>('', []);
	const trialDurationControl = useFormControl<string>('', []);
	const [trialSubscriptionEditContentVisible, setTrialSubscriptionEditContentVisible] = useState<boolean>(true);
	const editNonChargeDiffSubscription = isEdit && currentUser && !isNonChargeChannelSubscriptionType(currentUser.channelSubscriptions[0].subscriptionType);

	const channelDefaultTrialDays = channel?.defaultTrialDurationDays || config.trialDays;
	const trialDurationDays = !isSubscriptionExpired(channelSubscription) ?
		(channelSubscription && channelSubscription.trialDuration) || channelDefaultTrialDays :
		channelDefaultTrialDays;

	const channelPrice = channel.price.toString();

	const changeSubscriptionTypeByPayment = (subscriptionTypeByPayment: ChannelSubscriptionTypeByPayment) => {
		if (isFreeTrialSubscriptionTypeByPayment(subscriptionTypeByPayment)) {
			subscriptionTypeFormControl.setValue(ChannelSubscriptionType.Trial);
		} else {
			subscriptionTypeFormControl.setValue(undefined);
		}

		subscriptionTypeByPaymentFormControl.setValue(subscriptionTypeByPayment);
	}

	const createCancelAt = (value: any) => {
		switch (subscriptionTypeFormControl.value) {
		case ChannelSubscriptionType.Trial:
			return trialDurationControl.value
				? getDateEndOfTheDay(moment().add(trialDurationControl.value, 'days'))
				: getDateEndOfTheDay(moment().add(channel.defaultTrialDurationDays, 'days'))
					?? config.trialDays;
		default:
			return value.expiryDate ? getDateEndOfTheDay(value.expiryDate) : undefined;
		}
	}

	useEffect(() => {
		if (isAccountChannelSubscriptionType(subscriptionTypeFormControl.value)) {
			accountNumberControl.setDirty(false);
			accountNumberControl.setValidators([requiredValidator]);
		}

		if (isNonChargeChannelSubscriptionType(subscriptionTypeFormControl.value)) {
			accountNumberControl.setValidators([]);
		}

		if (isExternalBillingChannelSubscriptionType(subscriptionTypeFormControl.value)) {
			billingMethodControl.setValidators([requiredValidator])
		} else {
			billingMethodControl.setValidators([])
		}

		if (isTrialChannelSubscriptionType(subscriptionTypeFormControl.value)) {
			trialDurationControl.setValidators([
				(val) => minNumberOrEmptyValidator(val, config.minTrialDays),
				(val) => maxNumberValidator(val, config.maxTrialDays)]);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [subscriptionTypeFormControl.value]);

	useEffect(() => {
		// extra logic for highlight errors on dirty form
		if (subscriptionTypeByPaymentFormControl.value) {
			billingMethodControl.setDirty(false);
			accountNumberControl.setDirty(false);
			subscriptionTypeFormControl.setDirty(false);
			subscriptionTypeFormControl.setValidators([requiredValidator]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [subscriptionTypeByPaymentFormControl.value]);

	useEffect(() => {
		if (isEdit) {
			const cs = currentUser?.channelSubscriptions[0];

			if (!cs) return;

			setChannelSubscription(cs);

			currentUser && userFormControl.setValue({
				label: `${currentUser.name} (${currentUser.email})`,
				value: currentUser._id
			} as Option);

			notesFormControl.setValue(cs.notes);

			if (isTrialChannelSubscriptionType(cs.subscriptionType)) {
				subscriptionTypeByPaymentFormControl.setValue(ChannelSubscriptionTypeByPayment.FreeTrial);
			} else if ((isAccountChannelSubscriptionType(cs.subscriptionType) && cs.rebate) || isNonChargeChannelSubscriptionType(cs.subscriptionType)) {
				subscriptionTypeByPaymentFormControl.setValue(ChannelSubscriptionTypeByPayment.Free);
			} else {
				subscriptionTypeByPaymentFormControl.setValue(ChannelSubscriptionTypeByPayment.Paid);
			}

			const formattedDate = getChannelSubscriptionCancelAtDateToShow(cs);

			accountNumberControl.setValue(cs.accountNumber)
			expiryDateFormControl.setValue(formattedDate);
			subscriptionTypeFormControl.setValue(cs.subscriptionType);
			pricePerMonthControl.setValue(cs.pricePerMonth || '');
			isFullArticleContentControl.setValue(cs.isFullArticleContent || false);
			billingMethodControl.setValue(cs.billingMethod);
			if (!isSubscriptionExpired(cs)) trialDurationControl.setValue(cs.trialDuration || trialDurationDays);
		}
	}, [isEdit, channel])

	useEffect(() => {
		const subscriptionType = subscriptionTypeFormControl.value;
		const subscriptionTypeByPayment = subscriptionTypeByPaymentFormControl.value;
		const channelSubscription = currentUser?.channelSubscriptions[0];

		if ((isEdit && !channelSubscription) || !channel) return;

		if (!isEdit && isTrialChannelSubscriptionType(subscriptionType)) {
			trialDurationControl.setValue(channel.defaultTrialDurationDays || config.trialDays);
		}

		if (isEdit && channelSubscription) {
			/* account number and cancelAt for account subscription logic, when subscription type has been changed */
			if (isAccountChannelSubscriptionType(subscriptionType)) {
				if (channelSubscription.rebate) {
					if (isFreeSubscriptionTypeByPayment(subscriptionTypeByPayment)) {
						const formattedDate = getChannelSubscriptionCancelAtDateToShow(channelSubscription);

						accountNumberControl.setValue(channelSubscription.accountNumber);
						expiryDateFormControl.setValue(formattedDate);
					} else {
						accountNumberControl.setValue('');
						expiryDateFormControl.setValue(null);
					}
				} else {
					if (isPaidSubscriptionTypeByPayment(subscriptionTypeByPayment)) {
						const formattedDate = getChannelSubscriptionCancelAtDateToShow(channelSubscription);

						accountNumberControl.setValue(channelSubscription.accountNumber);
						expiryDateFormControl.setValue(formattedDate);
					} else {
						accountNumberControl.setValue('');
						expiryDateFormControl.setValue(null);
					}
				}
			}
			/* account number and cancelAt for account subscription logic, when subscription type has been changed */

			/* pricePerMonth and cancelAt logic, when subscription type has been changed */
			if (isSameChannelSubscriptionTypeSelected(channelSubscription.subscriptionType, subscriptionTypeFormControl.value)) {
				const formattedDate = getChannelSubscriptionCancelAtDateToShow(channelSubscription);

				pricePerMonthControl.setValue(channelSubscription.pricePerMonth);
				expiryDateFormControl.setValue(formattedDate);
			} else {
				pricePerMonthControl.setValue('');
				expiryDateFormControl.setValue(null);
			}
			/* pricePerMonth and cancelAt logic, when subscription type has been changed */
		} else {
			accountNumberControl.setValue('');
			pricePerMonthControl.setValue('');
			expiryDateFormControl.setValue(null);
		}
	}, [isEdit, channel, currentUser, subscriptionTypeFormControl.value, subscriptionTypeByPaymentFormControl.value])

	const addSubscriberEndpoint = () => {
		switch (subscriptionTypeFormControl.value) {
		case ChannelSubscriptionType.NonCharge:
			return CREATE_NON_CHARGE_CHANNEL_SUBSCRIPTION;
		case ChannelSubscriptionType.Account:
			return CREATE_ACCOUNT_CHANNEL_SUBSCRIPTION;
		case ChannelSubscriptionType.Trial:
			return CREATE_TRIAL_CHANNEL_SUBSCRIPTION;
		case ChannelSubscriptionType.External:
			return CREATE_EXTERNAL_BILLING_CHANNEL_SUBSCRIPTION;
		default:
			return null;
		}
	}

	const editSubscriberEndpoint = () => {
		switch (subscriptionTypeFormControl.value) {
		case ChannelSubscriptionType.NonCharge:
			return UPDATE_NON_CHARGE_CHANNEL_SUBSCRIPTION;
		case ChannelSubscriptionType.Account:
			return UPDATE_ACCOUNT_CHANNEL_SUBSCRIPTION;
		case ChannelSubscriptionType.Trial:
			return UPDATE_TRIAL_CHANNEL_SUBSCRIPTION;
		case ChannelSubscriptionType.External:
			return UPDATE_EXTERNAL_BILLING_CHANNEL_SUBSCRIPTION;
		default:
			return null;
		}
	}

	const deleteSubscriberEndpoint = () => {
		return CANCEL_NON_STRIPE_SUBSCRIPTION_IMMEDIATELY;
	}

	const form = useFormGroup({
		subscriptionTypeByPayment: subscriptionTypeByPaymentFormControl,
		subscriptionType: subscriptionTypeFormControl,
		...(isBulk ? {
			file: fileFormControl
		} : {
			user: userFormControl
		}),
		accountNumber: accountNumberControl,
		expiryDate: expiryDateFormControl,
		notes: notesFormControl,
		pricePerMonth: pricePerMonthControl,
		isFullArticleContent: isFullArticleContentControl,
		billingMethod: billingMethodControl,
		trialDuration: trialDurationControl
	});

	const getReqBody = useCallback(() => {
		const { value } = form;

		return {
			channelId,
			...(value.user ? {userId: value.user} : {}),
			cancelAt: createCancelAt(value),
			accountNumber: isAccountChannelSubscriptionType(subscriptionTypeFormControl.value) ? value.accountNumber : undefined,
			notes: value.notes,
			subscriptionType: value.subscriptionType,
			pricePerMonth: isExternalBillingChannelSubscriptionType(subscriptionTypeFormControl.value) ||
			(isAccountChannelSubscriptionType(subscriptionTypeFormControl.value) &&
				isPaidSubscriptionTypeByPayment(subscriptionTypeByPaymentFormControl.value)) ?
				value.pricePerMonth ? +value.pricePerMonth : channel.price : undefined,
			rebate: isAccountChannelSubscriptionType(subscriptionTypeFormControl.value) &&
			!isPaidSubscriptionTypeByPayment(subscriptionTypeByPaymentFormControl.value) ? true : undefined,
			isFullArticleContent: isFullArticleContentControl.value,
			billingMethod: isExternalBillingChannelSubscriptionType(subscriptionTypeFormControl.value)
				? billingMethodControl.value : undefined,
			addedByAdmin: !isBulk ? true : undefined
		};
	}, [form]);

	const {isNew, formMessage, formBusy, onSubmit, onCancel, onAlertBoxClose} = useManageTemplateForm(
		ManageTemplateInstance.ChannelSubscriber,
		form,
		isEdit,
		addSubscriberEndpoint(),
		editSubscriberEndpoint(),
		deleteSubscriberEndpoint(),
		returnUrl
	);

	const onBulkProceed = () => {
		form.setDirty();
		if (!form.isValid) return;
		proceedBulk(getReqBody());
	}

	const expiryDateField = (message?: string) => {
		return (
			<>
				<DatePicker
					id="subscriber-expiry-date-time-picker"
					format="dd/MM/yyyy"
					variant="inline"
					disableToolbar
					invalidDateMessage="Invalid Expiry Date"
					minDateMessage={MIN_DATE_MESSAGE}
					onChange={(date: Date | null) => {
						const formattedDate = date || null;
						expiryDateFormControl.setValue(formattedDate);
					}}
					value={expiryDateFormControl.value}
					label="Expiry"
					optional
					error={expiryDateFormControl.showError}
					errorText={expiryDateFormControl.validationMessage}
					disablePast={true}
				/>
				{message ? (
					<Box mt={1}>
						<TextBlock styleName="caption1" ink="muted">{message}</TextBlock>
					</Box>
				) : <></>}
			</>
		)
	};

	const pricePerMonthField = (
		<TextField
			id={'billing-method-eternal-method-input'}
			fullWidth
			required={false}
			prefix="$"
			error={pricePerMonthControl.showError}
			errorText={pricePerMonthControl.validationMessage}
			label="Bespoke monthly price"
			helperText="If you have a bespoke agreement with the Client, provide the monthly price that’s overriding the channel’s ask."
			inputMode="numeric"
			pattern="^[0-9]+(\.[0-9]{1,2})?$"
			placeholder={channelPrice}
			value={pricePerMonthControl.value}
			onKeyPress={e => (e?.key === '-' || e?.key === '+') && e.preventDefault()}
			onBlur={e => {
				if (pricePerMonthControl?.value) {
					pricePerMonthControl?.setValue(parseFloat(pricePerMonthControl?.value).toFixed(2));
				}
				pricePerMonthControl?.handleBlur(e);
			}}
			onChange={(e: ChangeEvent<any>): void => {
				const val: string = e?.target?.value;
				if (val?.length > 100) {
					return;
				}
				pricePerMonthControl?.setValue(val);
			}}
		/>
	)

	const accountNumberField = (
		<TextField
			id={'subscriber-account-input'}
			fullWidth
			helperText="The charge for this channel subscription will be shown as rebated on the client’s monthly statement."
			error={accountNumberControl.showError}
			errorText={accountNumberControl.validationMessage}
			type="text"
			label="Account number"
			autoComplete="off"
			value={accountNumberControl.value}
			onBlur={accountNumberControl.handleBlur}
			onChange={(e: ChangeEvent<any>): void => {
				accountNumberControl.setValue(e?.target?.value);
			}}
		/>
	);

	const paidSubscriptionTypeContent = (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Radio
					checked={isStripeChannelSubscriptionType(subscriptionTypeFormControl.value)}
					onChange={() => subscriptionTypeFormControl.setValue(ChannelSubscriptionType.Stripe)}
					value="v"
					label="Paying with Stripe"
					disabled={true}
				/>
			</Grid>
			<Grid item xs={12}>
				<Radio
					checked={isAccountChannelSubscriptionType(subscriptionTypeFormControl.value)}
					onChange={() => subscriptionTypeFormControl.setValue(ChannelSubscriptionType.Account)}
					value="v"
					label="Billed via Clearing Account"
				/>
			</Grid>
			{isAccountChannelSubscriptionType(subscriptionTypeFormControl.value) && (
				<>
					<Grid item xs={12}>{accountNumberField}</Grid>
					<Grid item xs={12}>{pricePerMonthField}</Grid>
					<Grid item xs={12}>{expiryDateField('The subscription will end on the day of expiry.')}</Grid>
				</>
			)}
			<Grid item xs={12}>
				<Radio
					checked={isExternalBillingChannelSubscriptionType(subscriptionTypeFormControl.value)}
					onChange={() => subscriptionTypeFormControl.setValue(ChannelSubscriptionType.External)}
					value="v"
					label="Billed externally"
				/>
			</Grid>
			{isExternalBillingChannelSubscriptionType(subscriptionTypeFormControl.value) && (
				<>
					<Grid item xs={12}>
						<TextField
							id={'billing-method-eternal-method-input'}
							fullWidth
							helperText="Provide details on how the client is charged and billed."
							type="text"
							label="Billing method"
							autoComplete="off"
							error={billingMethodControl.showError}
							errorText={billingMethodControl.validationMessage}
							value={billingMethodControl.value}
							onBlur={billingMethodControl.handleBlur}
							onChange={(e: ChangeEvent<any>): void => {
								billingMethodControl.setValue(e?.target?.value);
							}}
						/>
					</Grid>
					<Grid item xs={12}>{pricePerMonthField}</Grid>
					<Grid item xs={12}>{expiryDateField('The subscription will end on the day of expiry.')}</Grid>
				</>
			)}
			{subscriptionTypeFormControl.showError && (
				<Grid item xs={12}>
					<ErrorText>{subscriptionTypeFormControl.validationMessage}</ErrorText>
				</Grid>
			)}
		</Grid>
	);

	const freeSubscriptionTypeContent = (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Radio
					checked={isAccountChannelSubscriptionType(subscriptionTypeFormControl.value)}
					onChange={() => subscriptionTypeFormControl.setValue(ChannelSubscriptionType.Account)}
					value="v"
					label="Clearing client (rebated)"
				/>
			</Grid>
			{isAccountChannelSubscriptionType(subscriptionTypeFormControl.value) && (
				<>
					<Grid item xs={12}>{accountNumberField}</Grid>
					<Grid item xs={12}>
						{expiryDateField('The subscription will end on the day of expiry.')}
					</Grid>
				</>
			)}
			<Grid item xs={12}>
				<Radio
					checked={isNonChargeChannelSubscriptionType(subscriptionTypeFormControl.value)}
					onChange={() => subscriptionTypeFormControl.setValue(ChannelSubscriptionType.NonCharge)}
					value="v"
					label="Non-client"
				/>
			</Grid>
			{isNonChargeChannelSubscriptionType(subscriptionTypeFormControl.value) && (
				<>
					<Grid item xs={12}>
						<TextBlock styleName="caption1" ink="muted">
							Non-clients and internal users may be given access to read a premium channel temporarily or
							indefinitely.
						</TextBlock>
					</Grid>
					<Grid item xs={12}>
						{expiryDateField('The subscription will be cancelled on the day of expiry.')}
					</Grid>
				</>
			)}
			{subscriptionTypeFormControl.showError && (
				<Grid item xs={12}>
					<ErrorText>{subscriptionTypeFormControl.validationMessage}</ErrorText>
				</Grid>
			)}
		</Grid>
	);

	const freeTrialSubscriptionTypeContent = (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<TextBlock styleName="caption1" ink="muted">
					{
						isEdit && !isSubscriptionExpired(channelSubscription) ?
							'Adjust trial duration in number of days from today.' :
							'You can define a custom length for this user by providing a value below.'
					}
				</TextBlock>
			</Grid>
			<Grid item xs={12}>
				<TextField
					id={'trial-duration-input'}
					fullWidth
					required={false}
					postfix="days"
					placeholder={trialDurationDays.toString()}
					label="Trial duration"
					inputMode="numeric"
					pattern="^[0-9]+(\.[0-9]{1,2})?$"
					error={trialDurationControl.showError}
					errorText={trialDurationControl.validationMessage}
					value={trialDurationControl.value}
					onKeyPress={e => (e?.key === '-' || e?.key === '+') && e.preventDefault()}
					onBlur={e => {
						if (trialDurationControl?.value) {
							trialDurationControl?.setValue(+trialDurationControl?.value);
						}
						trialDurationControl?.handleBlur(e);
					}}
					onChange={(e: ChangeEvent<any>): void => {
						const val: string = e?.target?.value;
						if (val?.length > 100) {
							return;
						}
						trialDurationControl?.setValue(val);
					}}
				/>
				<Box mt={1}>
					<TextBlock styleName="caption1" ink="muted">
						Free trial ends
						on <b>{moment().add(trialDurationControl?.value || config.trialDays, 'days').format('DD MMM YYYY')}</b>
					</TextBlock>
				</Box>
			</Grid>
		</Grid>
	);

	const onTrialDurationButtonClick = () => setTrialSubscriptionEditContentVisible(false);

	const trialSubscriptionEditButton = (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<TextBlock styleName="caption1" ink="muted">
					{
						isSubscriptionExpired(channelSubscription) ?
							<>The free trial expired
								on <b>{moment(getStartOfTheDay(channelSubscription?.cancelAt)).format('DD MMM YYYY')}</b></> :
							<>This {trialDurationDays} day free trial expires
								on <b>{moment(getStartOfTheDay(channelSubscription?.cancelAt)).format('DD MMM YYYY')}</b></>
					}
				</TextBlock>
			</Grid>
			<Grid item xs={12}>
				<Button
					id="adjust-trial-duration-button"
					colorType="primary"
					size="medium"
					onClick={onTrialDurationButtonClick}
				>
					{isSubscriptionExpired(channelSubscription) ? 'Restart trial' : 'Adjust trial duration'}
				</Button>
			</Grid>
		</Grid>
	);

	return (
		<ManageTemplate
			pathArea={'Channels'}
			returnUrl={returnUrl}
			templateInstance={getPremiumChannelsSubscribersFormInstance(isBulk)}
			isNew={isNew}
			formMessage={formMessage}
			formBusy={formBusy}
			onCancel={onCancel}
			onSubmit={(ev: any): void => {
				!isBulk ? onSubmit(ev, getReqBody()) : onBulkProceed();
			}}
			onDelete={onDelete}
			onAlertBoxClose={onAlertBoxClose}
			templateCreateButtonLabel={getPremiumChannelsSubscribersFormCreateButtonLabel(isBulk)}
			templateUpdateButtonLabel={premiumChannelsSubscribersFormUpdateButtonLabel}
			templateDeleteButtonLabel={premiumChannelsSubscribersFormDeleteButtonLabel}
			templateDeleteDialogTitle={premiumChannelsSubscribersFormDeleteDialogTitle}
			breadcrumbOverride={getPremiumChannelsSubscribersFormBreadCrumbs(channel.name, returnUrl, isEdit, isBulk)}
			maxContentWidth={isEdit ? 450 : 420}
			helperContent={
				<TextBlock className={classes.helperText}>
					{
						editNonChargeDiffSubscription ? '' : isBulk ?
							'Add multiple subscriptions by providing a clean CSV file with list of email addresses' :
							'Set up a non-charge subscriber below. Paid subscribers must subscribe themselves through the Channel page on Insights.'
					}
				</TextBlock>
			}
		>
			{usersField}
			<ManageTemplateFormItem>
				<Grid container spacing={1}>
					<Grid item xs={12}>
						<RadioWithContent
							checked={isPaidSubscriptionTypeByPayment(subscriptionTypeByPaymentFormControl.value)}
							onChange={() => changeSubscriptionTypeByPayment(ChannelSubscriptionTypeByPayment.Paid)}
							label="Paid subscription"
							content={paidSubscriptionTypeContent}
						/>
					</Grid>
					<Grid item xs={12}>
						<RadioWithContent
							checked={isFreeSubscriptionTypeByPayment(subscriptionTypeByPaymentFormControl.value)}
							onChange={() => changeSubscriptionTypeByPayment(ChannelSubscriptionTypeByPayment.Free)}
							label="Free subscription"
							content={freeSubscriptionTypeContent}
						/>
					</Grid>
					<Grid item xs={12}>
						<RadioWithContent
							checked={isFreeTrialSubscriptionTypeByPayment(subscriptionTypeByPaymentFormControl.value)}
							onChange={() => changeSubscriptionTypeByPayment(ChannelSubscriptionTypeByPayment.FreeTrial)}
							label="Free trial"
							content={isEdit
								? (trialSubscriptionEditContentVisible
									? trialSubscriptionEditButton
									: freeTrialSubscriptionTypeContent)
								: freeTrialSubscriptionTypeContent}
						/>
					</Grid>
					{subscriptionTypeByPaymentFormControl.showError &&
						<Grid item xs={12}>
							<ErrorText>{subscriptionTypeByPaymentFormControl.validationMessage}</ErrorText>
						</Grid>
					}
				</Grid>
			</ManageTemplateFormItem>
			{notesField}
			<ManageTemplateFormItem>
				<Checkbox
					id="fullArticleContent-checkbox"
					label="Send full article content in email notifications"
					checked={isFullArticleContentControl?.value}
					value="a"
					onChange={(e: ChangeEvent<any>): void => {
						isFullArticleContentControl?.setValue(e?.target?.checked);
					}}
				/>
			</ManageTemplateFormItem>
		</ManageTemplate>
	)
}
